import React from 'react';
import { InfoCircleFilled, WarningFilled } from '@ant-design/icons';
import AlertCircle from '@assets/icons/alert-circle.svg';
import CheckCircleFilled from '@assets/icons/checkmark-circle.svg';
import { GLOBAL_ALERT_BANNER_TEXTS } from '@components/global-alert-banner/constants.ts';
import { useAppDispatch, useAppSelector } from '@hooks/reduxHooks.ts';
import { getUserPreferencesOrDefaults } from '@routes/___shared___/utils.ts';
import { clearAlert } from '@shared/model/alert.slice.ts';
import { formatDateTimeBasedOnPreferences as formatDate } from '@utils/date.ts';

import { BaseAlertBanner as AlertBanner } from '../alert-banner';
import { AutoClose } from '../auto-close';
import { CustomGlobalAlertBannerWrapper, GeneralGlobalAlertBannerWrapper } from './styled.tsx';

const ICONS: Record<string, React.ReactNode> = {
    success: <CheckCircleFilled />,
    info: <InfoCircleFilled />,
    warning: <WarningFilled />,
    error: <AlertCircle />,
};

const MaintenanceDescription = ({
    dateFormat,
    timeFormat,
    maintenanceStartDate = '',
    maintenanceEndDate = '',
}: {
    dateFormat: string;
    timeFormat: string;
    maintenanceStartDate?: string;
    maintenanceEndDate?: string;
}) => {
    return (
        <div>
            {GLOBAL_ALERT_BANNER_TEXTS.MAINTENANCE_INTRO()}&nbsp;
            <strong>
                {GLOBAL_ALERT_BANNER_TEXTS.UNAVAILABLE_ON()} {formatDate(maintenanceStartDate, dateFormat, timeFormat)}{' '}
                {GLOBAL_ALERT_BANNER_TEXTS.TO()} {formatDate(maintenanceEndDate, dateFormat, timeFormat)}
            </strong>
            &nbsp;{GLOBAL_ALERT_BANNER_TEXTS.MAINTENANCE_OUTRO()}
        </div>
    );
};

const GlobalAlertBanner: React.FC = () => {
    const TITLE = GLOBAL_ALERT_BANNER_TEXTS.TITLE();

    const dispatch = useAppDispatch();

    const healthStatus = useAppSelector((state) => state.health.healthStatus);
    const { plannedMaintenanceUTC, expectedEndOfMaintenanceUTC, isMaintenance } = healthStatus?.maintenanceInfo || {};
    const userFullInfo = useAppSelector((state) => state.user.userInfo);
    const prefs = getUserPreferencesOrDefaults(userFullInfo);
    const { preferredDateFormat, preferredTimeFormat } = prefs;

    // Custom global alerts
    const customAlert = useAppSelector((state) => state.alert.current);

    if (plannedMaintenanceUTC && !isMaintenance) {
        return (
            <GeneralGlobalAlertBannerWrapper>
                <div id="banner">
                    <AlertBanner
                        title={TITLE}
                        description={
                            <MaintenanceDescription
                                dateFormat={preferredDateFormat}
                                timeFormat={preferredTimeFormat}
                                maintenanceStartDate={plannedMaintenanceUTC!}
                                maintenanceEndDate={expectedEndOfMaintenanceUTC!}
                            />
                        }
                        type="warning"
                        icon={<WarningFilled />}
                        closable
                    />
                </div>
            </GeneralGlobalAlertBannerWrapper>
        );
    }

    if (customAlert) {
        return (
            <CustomGlobalAlertBannerWrapper>
                <div id="custom-banner">
                    <AutoClose onClose={() => dispatch(clearAlert())}>
                        <AlertBanner
                            type={customAlert.type}
                            title={customAlert.message}
                            description={customAlert.description}
                            icon={ICONS[customAlert.type]}
                            closable={{
                                onClose: () => dispatch(clearAlert()),
                            }}
                        />
                    </AutoClose>
                </div>
            </CustomGlobalAlertBannerWrapper>
        );
    }

    return null;
};

export default GlobalAlertBanner;
